/** Mirrors packages/domain terminology helpers — kept local so the app bundle skips TypeORM. */

export type OrgFeatureRow = {
  featureKey: string;
  displayName?: string | null;
  isEnabled: boolean;
};

export const TERMINOLOGY_MODULES = ['customer', 'vendor', 'product'] as const;
export type TerminologyModule = (typeof TERMINOLOGY_MODULES)[number];

export type ModuleTerminology = {
  singular: string;
  plural: string;
};

export function resolveModuleDisplayName(
  orgFeatures: OrgFeatureRow[] | null | undefined,
  module: string,
): string | null {
  if (!orgFeatures?.length) return null;
  const prefix = `${module}.`;
  const rows = orgFeatures.filter(
    (f) => f.featureKey === module || f.featureKey.startsWith(prefix),
  );
  const preferView = rows.find(
    (r) => r.featureKey === `${module}.view` && String(r.displayName ?? '').trim(),
  );
  if (preferView?.displayName?.trim()) return preferView.displayName.trim();
  const any = rows.find((r) => String(r.displayName ?? '').trim());
  return any?.displayName?.trim() ?? null;
}

/**
 * Singular + plural labels. Org displayName override applies to both when set
 * (admin-chosen term). Otherwise use provided i18n fallbacks.
 */
export function resolveModuleTerminology(
  orgFeatures: OrgFeatureRow[] | null | undefined,
  module: string,
  fallbacks: ModuleTerminology,
): ModuleTerminology {
  const override = resolveModuleDisplayName(orgFeatures, module);
  if (override) {
    return { singular: override, plural: override };
  }
  return fallbacks;
}

export function isModuleEntitled(
  orgFeatures: OrgFeatureRow[] | null | undefined,
  featureKeys: string[] | null | undefined,
  module: string,
): boolean {
  const prefix = `${module}.`;
  const roleKeys = featureKeys ?? [];
  const roleHas = roleKeys.some((k) => k === module || k.startsWith(prefix));

  if (!orgFeatures?.length) return roleHas;

  const orgHas = orgFeatures.some(
    (f) => f.isEnabled && (f.featureKey === module || f.featureKey.startsWith(prefix)),
  );
  return orgHas && roleHas;
}

export type ModuleTermMap = Record<TerminologyModule, ModuleTerminology>;

/**
 * Substitute default English Customer/Vendor/Product words in template titles
 * with org terminology (plural forms first to avoid partial matches).
 */
export function applyModuleTermsToText(text: string, terms: ModuleTermMap): string {
  if (!text) return text;
  let out = text;
  const pairs: Array<[RegExp, string]> = [
    [/\bCustomers\b/g, terms.customer.plural],
    [/\bCustomer\b/g, terms.customer.singular],
    [/\bVendors\b/g, terms.vendor.plural],
    [/\bVendor\b/g, terms.vendor.singular],
    [/\bProducts\b/g, terms.product.plural],
    [/\bProduct\b/g, terms.product.singular],
  ];
  for (const [re, replacement] of pairs) {
    if (replacement) out = out.replace(re, replacement);
  }
  return out;
}
